import {observer} from 'mobx-react';
import React from 'react';
import {HistoryItem as IHistoryItem, MemoryItemConversionType} from '../../stores/MemoryBarStore';
import moment from 'moment';
import {TProvider} from '../../i18n/i18n';
import {classes, stylesheet} from 'typestyle';
import MemoryItemOptionRows from './MemoryItemOptionRows';
import {px} from 'csx';
import {NestedCSSProperties} from 'typestyle/lib/types';
import {KPI} from '../../kpi';
import {BlackColorFaded10, BlackColorFaded20, BlackColorFaded40, GreyBlueBackgroundColor, WhiteColor} from '../../styles/colors';

interface HistoryItemProps {
    disabled?: boolean;
    data: IHistoryItem;
    index: number;
    memoryItemConversionOccured: (conversionType: MemoryItemConversionType) => void;
    onClick?: (historyItem: IHistoryItem) => void;
    getAttributeNameIfPossible: (kpi: KPI) => string;
    addBookmarkEntry: (historyItem: IHistoryItem, bookmarkName: string) => void;
}

@observer
export default class HistoryItem extends React.Component<HistoryItemProps> {
    public state = {
        nameEntryVisible: false,
    };

    private getNiceDate() {
        const timestampDate = moment(this.props.data.timestamp);
        return moment().diff(timestampDate, 'days') === 0 ? timestampDate.format('LT') : timestampDate.format('LLL');
    }

    // tslint:disable-next-line:no-identical-functions
    private onApplyHistorySettingsClick = (e: React.MouseEvent) => {
        if (this.props.onClick && !this.props.disabled) {
            e.preventDefault();
            e.stopPropagation();
            this.props.onClick(this.props.data);
        }
    };

    private onMakeBookmarkClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.props.disabled) {
            return;
        }

        this.setState({nameEntryVisible: true});
    };

    private closeNameEntryField = () => {
        this.setState({nameEntryVisible: false});
    };

    public render() {
        return (
            <div className={classes(historyCss.item, this.props.disabled ? 'disabled' : undefined)} onClick={this.onApplyHistorySettingsClick}>
                <div className={historyCss.timestamp}>{this.getNiceDate()}</div>
                <div className={historyCss.convertToBookmark} onClick={this.onMakeBookmarkClick}>
                    <i className="fa fa-star-o" />
                </div>
                {this.state.nameEntryVisible ? (
                    <BookmarkNameInput
                        addBookmarkEntry={this.props.addBookmarkEntry}
                        memoryItemConversionOccured={this.props.memoryItemConversionOccured}
                        closeNameEntryField={this.closeNameEntryField}
                        data={this.props.data}
                    />
                ) : null}
                <MemoryItemOptionRows {...this.props.data} getAttributeNameIfPossible={this.props.getAttributeNameIfPossible} />
            </div>
        );
    }
}

class BookmarkNameInput extends React.Component<
    {
        data: IHistoryItem;
        addBookmarkEntry: (historyItem: IHistoryItem, bookmarkName: string) => void;
        closeNameEntryField: () => void;
        memoryItemConversionOccured: (conversionType: MemoryItemConversionType) => void;
    },
    {text: string}
> {
    public state = {
        text: '',
    };
    public input?: HTMLInputElement | null = null;

    public componentDidMount() {
        if (this.input) {
            this.input.focus();
        }
    }

    private onSinkClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    private onConvertToBookmarkClick = () => {
        if (this.isBookmarkNameValid()) {
            this.props.addBookmarkEntry(this.props.data, this.state.text);
            this.props.closeNameEntryField();
            this.props.memoryItemConversionOccured(MemoryItemConversionType.TO_FAVORITE);
        }
    };

    private isBookmarkNameValid() {
        return this.state.text.length !== 0;
    }

    private onCloseNameEntryFieldClick = () => {
        this.props.closeNameEntryField();
    };

    private onKeyDown = (e: React.KeyboardEvent) => {
        e.which === 13 ? this.onConvertToBookmarkClick() : null;
    };

    public render() {
        return (
            <div>
                <div className={historyCss.addName} onClick={this.onSinkClick}>
                    <TProvider>
                        {(t) => (
                            <input
                                className={historyCss.nameInput}
                                ref={(input: HTMLInputElement | null) => {
                                    this.input = input;
                                }}
                                placeholder={t('memoryItem.favoriteName')}
                                type="text"
                                value={this.state.text}
                                onKeyDown={this.onKeyDown}
                                onChange={(e) => {
                                    this.setState({text: e.target.value});
                                }}
                            />
                        )}
                    </TProvider>
                    <div>
                        <div className={historyCss.saveFavoriteName} onClick={this.onCloseNameEntryFieldClick}>
                            <i className="fa fa-times" />
                        </div>
                        <div
                            className={classes(historyCss.saveFavoriteName, this.isBookmarkNameValid() ? null : historyCss.nameIsInvalid)}
                            onClick={this.onConvertToBookmarkClick}>
                            <i className="fa fa-check" />
                        </div>
                    </div>
                </div>
                <div className={historyCss.backdrop} />
            </div>
        );
    }
}

const convertButton: NestedCSSProperties = {
    cursor: 'pointer',
    borderRadius: 2,
    width: 25,
    height: 25,
    zIndex: 2,
    lineHeight: px(25),
    textAlign: 'center',
    position: 'absolute',
    right: 0,
    top: 0,
    fontSize: px(14),
    $nest: {
        '&:hover': {
            background: BlackColorFaded10,
        },
    },
};

export const historyCss = stylesheet({
    addName: {
        zIndex: 2,
        borderRadius: 2,
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translateX(-50%) translateY(-50%)',
        width: 200,
        padding: 5,
        background: BlackColorFaded40,
    },
    nameInput: {
        width: '100%',
        paddingLeft: 5,
        border: 'none',
        borderRadius: 2,
        marginBottom: 5,
    },
    saveFavoriteName: {
        cursor: 'pointer',
        width: '92.5px',
        textAlign: 'center',
        background: WhiteColor,
        display: 'inline-block',
        borderRadius: 2,
        $nest: {
            '&:last-child': {
                marginLeft: 5,
            },
        },
    },
    nameIsInvalid: {
        background: BlackColorFaded20,
    },
    backdrop: {
        position: 'absolute',
        background: BlackColorFaded20,
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    },
    item: {
        paddingTop: 15,
        paddingLeft: 5,
        paddingBottom: 5,
        paddingRight: 5,
        position: 'relative',
        minHeight: 42,
        marginLeft: 5,
        marginRight: 5,
        marginBottom: 5,
        backgroundColor: GreyBlueBackgroundColor,
        borderRadius: 2,
        border: '1px solid ' + GreyBlueBackgroundColor,
        $nest: {
            '&.disabled': {
                opacity: 0.6,
            },
        },
    },
    timestamp: {
        position: 'absolute',
        top: 0,
        left: 0,
        fontSize: px(11),
    },
    convertToBookmark: convertButton,
});



// WEBPACK FOOTER //
// ./src/views/MemoryBar/HistoryItem.tsx